import React, { useState } from 'react';
import previewDelirium from '../images/delirium.png';
import previewFairstitch from '../images/fairstitch.png';
import previewLilcart from '../images/lilcart.png';
import previewVenture from '../images/ventureattic.png';
import { Waves } from '../components/wave-background.tsx';

const workItems = [
  {
    title: 'FairStitch',
    href: 'https://fair-stitch.com/',
    image: previewFairstitch,
  },
  {
    title: 'Delirium',
    href: 'https://delirium-eecaterinas-projects.vercel.app/',
    image: previewDelirium,
  },
  {
    title: 'VentureAttic',
    href: 'https://ventureattic.com/',
    image: previewVenture,
  },
  {
    title: 'Lilcart',
    href: 'https://www.lilcart.xyz/',
    image: previewLilcart,
  },
];

const styles = `
  .works-page {
    position: relative;
    min-height: 100vh;
    overflow: hidden;
    background: #000000;
    color: #ffffff;
    font-family: 'Avenir Next', 'Helvetica Neue', 'Segoe UI', sans-serif;
  }

  .works-page * {
    box-sizing: border-box;
  }

  .works-layer {
    position: relative;
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: clamp(1.5rem, 3vw, 3rem);
    isolation: isolate;
  }

  .works-carousel-shell {
    position: relative;
    z-index: 2;
    width: min(920px, 100%);
    display: grid;
    justify-items: center;
    gap: 1.5rem;
  }

  .works-carousel {
    position: relative;
    width: min(100%, 780px);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .works-card-link {
    display: block;
    width: min(100%, 640px);
    text-decoration: none;
    color: inherit;
  }

  .works-card {
    overflow: hidden;
    border: 2px solid rgba(255, 255, 255, 0.72);
    border-radius: 36px;
    background: rgba(12, 12, 12, 0.6);
    box-shadow: 0 24px 64px rgba(0, 0, 0, 0.34);
    backdrop-filter: blur(10px);
  }

  .works-card-image {
    display: block;
    width: 100%;
    aspect-ratio: 16 / 10;
    object-fit: cover;
  }

  .works-card-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 1rem 1.25rem 1.2rem;
  }

  .works-card-title {
    font-size: clamp(1.1rem, 1.4vw, 1.35rem);
    font-weight: 600;
    letter-spacing: -0.03em;
  }

  .works-card-meta {
    color: rgba(255, 255, 255, 0.72);
    font-size: 0.92rem;
  }

  .works-arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 3.5rem;
    height: 3.5rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid rgba(255, 255, 255, 0.35);
    border-radius: 999px;
    background: rgba(0, 0, 0, 0.48);
    color: #ffffff;
    cursor: pointer;
    backdrop-filter: blur(10px);
    transition: transform 0.2s ease, background-color 0.2s ease;
    z-index: 3;
  }

  .works-arrow:hover,
  .works-arrow:focus-visible {
    background: rgba(0, 0, 0, 0.72);
    transform: translateY(-50%) scale(1.04);
  }

  .works-arrow--left {
    left: max(-1.75rem, -4vw);
  }

  .works-arrow--right {
    right: max(-1.75rem, -4vw);
  }

  .works-counter {
    position: relative;
    z-index: 2;
    color: rgba(255, 255, 255, 0.78);
    font-size: 0.95rem;
    letter-spacing: 0.12em;
    text-transform: uppercase;
  }

  .works-dots {
    display: flex;
    gap: 0.55rem;
  }

  .works-dot {
    width: 0.65rem;
    height: 0.65rem;
    border-radius: 999px;
    border: 0;
    background: rgba(255, 255, 255, 0.28);
    cursor: pointer;
    transition: transform 0.2s ease, background-color 0.2s ease;
  }

  .works-dot.is-active {
    background: #ffffff;
    transform: scale(1.1);
  }

  @media (max-width: 900px) {
    .works-carousel {
      width: 100%;
    }

    .works-arrow--left {
      left: 0.5rem;
    }

    .works-arrow--right {
      right: 0.5rem;
    }
  }

  @media (max-width: 640px) {
    .works-layer {
      padding: 1rem;
    }

    .works-card {
      border-radius: 24px;
    }

    .works-card-footer {
      flex-direction: column;
      align-items: flex-start;
    }

    .works-arrow {
      width: 3rem;
      height: 3rem;
    }
  }
`;

const Works = () => {
  const [activeIndex, setActiveIndex] = useState(0);

  const goToPrevious = () => {
    setActiveIndex((current) => (current - 1 + workItems.length) % workItems.length);
  };

  const goToNext = () => {
    setActiveIndex((current) => (current + 1) % workItems.length);
  };

  const activeItem = workItems[activeIndex];

  return (
    <>
      <style>{styles}</style>
      <main className="works-page">
        <div className="works-layer">
          <Waves strokeColor="#ffffff" backgroundColor="#000000" pointerSize={0.5} />

          <section className="works-carousel-shell" aria-label="Project carousel">
            <div className="works-carousel">
              <button
                className="works-arrow works-arrow--left"
                type="button"
                onClick={goToPrevious}
                aria-label="Show previous project"
              >
                &#8592;
              </button>

              <a
                className="works-card-link"
                href={activeItem.href}
                target="_blank"
                rel="noreferrer"
                aria-label={`Open ${activeItem.title} in a new tab`}
              >
                <article className="works-card">
                  <img className="works-card-image" src={activeItem.image} alt={activeItem.title} />
                  <div className="works-card-footer">
                    <div className="works-card-title">{activeItem.title}</div>
                    <div className="works-card-meta">Open website</div>
                  </div>
                </article>
              </a>

              <button
                className="works-arrow works-arrow--right"
                type="button"
                onClick={goToNext}
                aria-label="Show next project"
              >
                &#8594;
              </button>
            </div>

            <div className="works-counter">
              {String(activeIndex + 1).padStart(2, '0')} / {String(workItems.length).padStart(2, '0')}
            </div>

            <div className="works-dots" aria-label="Carousel pagination">
              {workItems.map((item, index) => (
                <button
                  key={item.title}
                  type="button"
                  className={`works-dot${index === activeIndex ? ' is-active' : ''}`}
                  onClick={() => setActiveIndex(index)}
                  aria-label={`Show ${item.title}`}
                />
              ))}
            </div>
          </section>
        </div>
      </main>
    </>
  );
};

export default Works;
